import React, { useCallback, ComponentType } from "react";
import { ApolloClient } from "@apollo/client";
import { useLoginMutation, useSocialLoginMutation, useRenewTokenMutation, useLogoutMutation, RenewTokenDocument, SocialProvider } from "../generated/graphql";
import { getAccessToken, setAccessToken, clearAccessToken, isTokenExpired, tokenNeedsRenewal } from "../utils/token";
import { clearAttempted, clearLoginBounces, clearAuthAttempts } from "../utils/silentAuth";

// The auth mutation documents live on the server-stack schema (/api/v3/graphql)
// and are declared in ./graphql/auth.graphql.tsx so graphql-codegen emits the
// typed hooks (useLoginMutation, …) consumed below. During the session → JWT
// dual-mode window the login mutation still creates the shared legacy session,
// so the main /graphql API (QUERY_ME and most admin views) keeps working with
// the session cookie while server-stack requests use the Bearer token.

export interface AuthUser {
  id: string;
  email: string;
  name?: string | null;
  roles?: string[] | null;
  accessToken: string;
}

// serverStackClientV2 lives in ../apollo-client/client, which imports
// handleAuthError from this module — require it lazily to break the import
// cycle (the module cache
// makes this a no-op after the first call).
const getClient = (): ApolloClient<object> => require("../apollo-client/client").serverStackClientV2;

export interface AuthApi {
  loginWithPassword: (email: string, password: string) => Promise<AuthUser | null>;
  performSocialLogin: (provider: string, credential: string) => Promise<AuthUser | null>;
  logoutJwt: () => Promise<void>;
  renewTokenIfNeeded: () => void;
}

// All server-stack auth mutations run through the generated hooks bound to
// serverStackClientV2 (not the default V3 provider client, which targets a
// different endpoint).
export const useAuth = (): AuthApi => {
  const client = getClient();
  const [loginMutation] = useLoginMutation({ client });
  const [socialLoginMutation] = useSocialLoginMutation({ client });
  const [logoutMutation] = useLogoutMutation({ client });
  const [renewMutation] = useRenewTokenMutation({ client });

  const loginWithPassword = useCallback(
    async (email: string, password: string) => {
      const { data } = await loginMutation({ variables: { input: { email, password } } });
      const user = (data && data.login) || null;
      if (user && user.accessToken) setAccessToken(user.accessToken);
      return user as AuthUser | null;
    },
    [loginMutation]
  );

  const performSocialLogin = useCallback(
    async (provider: string, credential: string) => {
      const { data } = await socialLoginMutation({ variables: { input: { provider: provider as SocialProvider, credential } } });
      const user = (data && data.socialLogin) || null;
      if (user && user.accessToken) setAccessToken(user.accessToken);
      return user as AuthUser | null;
    },
    [socialLoginMutation]
  );

  // Revokes the presented token server-side (and, while dual-mode is active,
  // destroys the live session) then clears the local token. Best-effort: the
  // local token is cleared even if the mutation fails.
  const logoutJwt = useCallback(async () => {
    try {
      await logoutMutation();
    } catch (e) {
      // ignore — token is cleared locally regardless
    }
    clearAccessToken();
    // Signing out ends the handshake's history too, so the next sign-in starts
    // clean instead of inheriting a dead end or a used-up trip.
    clearAttempted();
    clearLoginBounces();
    clearAuthAttempts();
  }, [logoutMutation]);

  const renewTokenIfNeeded = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;
    if (isTokenExpired(token)) return clearAccessToken();
    if (!tokenNeedsRenewal(token)) return;
    renewMutation()
      .then(({ data }) => {
        const next = data && data.renewToken && data.renewToken.accessToken;
        if (next) setAccessToken(next);
      })
      .catch(err => {
        // Clear only on a real auth rejection; a network failure must not log out.
        if (err && err.graphQLErrors && err.graphQLErrors.length) clearAccessToken();
      });
  }, [renewMutation]);

  return { loginWithPassword, performSocialLogin, logoutJwt, renewTokenIfNeeded };
};

// HOC injecting the auth api as an `auth` prop, for class components that can't
// call useAuth directly.
export function withAuth<P extends { auth?: AuthApi }>(Wrapped: ComponentType<P>) {
  const WithAuth = (props: Omit<P, "auth">) => {
    const auth = useAuth();
    return React.createElement(Wrapped, { ...(props as P), auth });
  };
  WithAuth.displayName = `withAuth(${Wrapped.displayName || Wrapped.name || "Component"})`;
  return WithAuth;
}

// Leaves the handshake markers alone on purpose. This fires on every auth
// rejection, so clearing them here would re-arm the trip to the user site each
// time round and the two sites would bounce forever. Only an explicit sign-out
// (or Try again) means "start over".
const forceLogout = () => {
  clearAccessToken();
  if (window.location.pathname === "/login") return;
  const redirect = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/login?redirect=${redirect}`;
};

let verifyingToken = false;

// Called from the Apollo error links — which run outside React, so this must
// stay an imperative function (no hooks) and drive the renewToken mutation via
// the client directly. "Forbidden resource" is thrown both for
// invalid/expired/revoked tokens AND for role rejections on a valid token, so
// before forcing a logout the token is verified via renewToken: success means
// the token is fine (permission issue — keep the user logged in); a GraphQL
// rejection means the token is truly invalid/revoked (forced logout).
export const handleAuthError = (graphQLErrors: readonly any[]) => {
  const isAuthError = (graphQLErrors || []).some(
    e =>
      (e.extensions && (e.extensions.code === "UNAUTHENTICATED" || e.extensions.code === "E_UNAUTHORIZED" || e.extensions.code === "FORBIDDEN")) ||
      (e.extensions && e.extensions.originalError && e.extensions.originalError.statusCode === 403) ||
      e.message === "Unauthorized" ||
      e.message === "Forbidden resource"
  );
  if (!isAuthError) return;
  const token = getAccessToken();
  if (!token) return forceLogout();
  if (isTokenExpired(token)) return forceLogout();
  if (verifyingToken) return;
  verifyingToken = true;
  getClient()
    .mutate({ mutation: RenewTokenDocument })
    .then(({ data }) => {
      verifyingToken = false;
      const next = data && data.renewToken && data.renewToken.accessToken;
      if (next) setAccessToken(next);
    })
    .catch(err => {
      verifyingToken = false;
      if (err && err.graphQLErrors && err.graphQLErrors.length) forceLogout();
    });
};
